import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istMaterialToken, karteAusRpc } from './materialKarte';
import { nameAusProfil } from './materialMeldenLogic';
import type {
    MaterialKarte,
    MaterialMeldungKurz,
    MeldenAntwort,
    MeldenFehler,
    MeldenMail,
} from './materialTypes';

/**
 * Die öffentliche Meldeseite `<origin>/m/<token>` (docs/materialverwaltung-plan.md):
 * Karte lesen über die anon-RPC `material_karte_public` (Nr. 179), melden über
 * die Server-Route POST `/api/material-melden` — `material_melden` darf nur
 * die service_role ausführen.
 *
 * RPC-Name und Parameter EXAKT wie im SQL (sonst PGRST202 und die Seite bricht
 * still). Die Karte trägt nie Kontaktdaten; der Melder erscheint nur als
 * „Max M.“.
 */

type Zeile = Record<string, unknown>;

const objekt = (x: unknown): Zeile | null =>
    x !== null && typeof x === 'object' && !Array.isArray(x) ? (x as Zeile) : null;

const text = (x: unknown): string | null => (typeof x === 'string' && x.trim() !== '' ? x : null);

/** jsonb einer RPC: PostgREST liefert ein Objekt, manchmal eine Zeichenkette. */
const alsJson = (data: unknown): unknown => {
    if (typeof data !== 'string') return data;
    try {
        return JSON.parse(data);
    } catch {
        return null;
    }
};

const fehlerCode = (e: unknown): string | null => {
    const code = objekt(e)?.code;
    return typeof code === 'string' && code ? code : null;
};

/** Migration Nr. 179 noch nicht gefahren — für den Besucher dasselbe wie eine ungültige Karte. */
const istFehlendeFunktion = (e: unknown): boolean => {
    const code = fehlerCode(e);
    const msg = text(objekt(e)?.message) ?? '';
    return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(msg);
};

// ─── Karte ───────────────────────────────────────────────────────────────────

/**
 * Karte zum Token (RPC `material_karte_public`). null bei falscher Form (ohne
 * Abfrage), unbekanntem/deaktiviertem Token, unlesbarer Antwort oder fehlender
 * Migration. Netz- und Serverfehler werfen — die Seite bietet dann „Erneut
 * versuchen“.
 */
export const ladeKarte = async (token: string): Promise<MaterialKarte | null> => {
    if (!istMaterialToken(token)) return null;
    try {
        const { data, error } = await supabase.rpc('material_karte_public', { p_token: token });
        if (error) throw error;
        return karteAusRpc(alsJson(data));
    } catch (e) {
        if (istFehlendeFunktion(e)) return null;
        throw new Error(describeError(e));
    }
};

// ─── Sitzung ─────────────────────────────────────────────────────────────────

/** JWT und Nutzer-ID aus `auth.getSession()`; keine oder unvollständige Sitzung → null. */
export const sitzungAus = (roh: unknown): { jwt: string; userId: string } | null => {
    const session = objekt(objekt(objekt(roh)?.data)?.session);
    const jwt = text(session?.access_token);
    const userId = text(objekt(session?.user)?.id);
    return jwt && userId ? { jwt, userId } : null;
};

/** Ist dieser Browser angemeldet? Dann meldet die Seite mit Profilname und JWT. */
export interface MeldeSitzung {
    /** „Vor- Nachname“ aus dem eigenen Profil; null, wenn es nicht lesbar ist. */
    name: string | null;
}

/**
 * Sitzung im Browser (z. B. wer auch die App auf dem Handy nutzt) und Name aus
 * dem eigenen Profil. Wirft nie: ohne lesbare Sitzung meldet die Seite anonym
 * mit dem freiwilligen Namensfeld.
 */
export const ladeSitzung = async (): Promise<MeldeSitzung | null> => {
    try {
        const s = sitzungAus(await supabase.auth.getSession());
        if (!s) return null;
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('first_name, last_name')
                .eq('id', s.userId)
                .maybeSingle();
            if (error) return { name: null };
            const p = objekt(data);
            return { name: nameAusProfil(p?.first_name, p?.last_name) };
        } catch {
            return { name: null };
        }
    } catch {
        return null;
    }
};

/**
 * Frisches JWT direkt vor dem Senden — `getSession()` erneuert ein
 * abgelaufenes selbst. Wirft nie; null heißt: anonym melden.
 */
export const aktuellesJwt = async (): Promise<string | null> => {
    try {
        return sitzungAus(await supabase.auth.getSession())?.jwt ?? null;
    } catch {
        return null;
    }
};

// ─── Melden ──────────────────────────────────────────────────────────────────

const meldungAusAntwort = (roh: unknown): MaterialMeldungKurz | null | undefined => {
    if (roh === null || roh === undefined) return null;
    const m = objekt(roh);
    if (!m) return undefined;
    const gemeldetAm = text(m.gemeldetAm);
    const zuletztAusgeloestAm = text(m.zuletztAusgeloestAm);
    const gesperrtBis = text(m.gesperrtBis);
    const status = m.status;
    if (!gemeldetAm || !zuletztAusgeloestAm || !gesperrtBis) return undefined;
    if (status !== 'offen' && status !== 'bestellt') return undefined;
    const anzahl = typeof m.anzahl === 'number' && Number.isFinite(m.anzahl) && m.anzahl >= 1 ? Math.floor(m.anzahl) : 1;
    return {
        gemeldetAm,
        zuletztAusgeloestAm,
        anzahl,
        melderKurz: text(m.melderKurz),
        status,
        bestelltAm: text(m.bestelltAm),
        gesperrtBis,
    };
};

/**
 * Die Karte in der Antwort der Route — dort schon als `MaterialKarte`
 * (camelCase, `karteAusRpc` lief auf dem Server). Liefert die Route doch die
 * rohe RPC-Form (snake_case), wird sie hier gelesen statt verworfen.
 */
export const karteAusAntwort = (roh: unknown): MaterialKarte | null => {
    const k = objekt(roh);
    const a = objekt(k?.artikel);
    if (k && a) {
        const id = text(a.id);
        const name = text(a.name);
        const meldung = meldungAusAntwort(k.meldung);
        if (id && name && meldung !== undefined) {
            return {
                artikel: { id, name, lagerort: text(a.lagerort), hinweis: text(a.hinweis) },
                firma: text(k.firma) ?? '',
                meldung,
            };
        }
    }
    return karteAusRpc(roh);
};

const FEHLER: readonly MeldenFehler[] = ['token', 'ungueltig', 'zu_viele', 'server'];
const istFehler = (x: unknown): x is MeldenFehler => FEHLER.includes(x as MeldenFehler);
const mailAus = (x: unknown): MeldenMail => (x === 'gesendet' || x === 'fehler' ? x : 'keine');

/**
 * Rumpf + Status der Route → `MeldenAntwort`. Maßgeblich ist der Rumpf; ohne
 * lesbaren Rumpf (HTML-Fehlerseite von Vercel, Proxy) zählt nur 429 als
 * „zu viele“ — ein nacktes 404 heißt eher „Route fehlt“ als „Karte ungültig“
 * und wird deshalb ein Serverfehler mit HTTP-Code.
 */
export const meldenAntwortLesen = (status: number, body: unknown): MeldenAntwort => {
    const o = objekt(body);
    if (o?.ok === true) {
        const art = o.art;
        const karte = karteAusAntwort(o.karte);
        if ((art === 'neu' || art === 'erinnerung' || art === 'gesperrt') && karte) {
            return { ok: true, art, karte, mail: mailAus(o.mail) };
        }
        return {
            ok: false,
            fehler: 'server',
            text: 'Die Antwort des Servers war unvollständig. Bitte „Erneut versuchen“ – die Seite zeigt dann den aktuellen Stand.',
        };
    }
    if (o?.ok === false && istFehler(o.fehler)) {
        if (o.fehler !== 'server') return { ok: false, fehler: o.fehler };
        const t = text(o.text);
        return t ? { ok: false, fehler: 'server', text: t } : { ok: false, fehler: 'server' };
    }
    if (status === 429) return { ok: false, fehler: 'zu_viele' };
    return { ok: false, fehler: 'server', text: `Der Server antwortet nicht wie erwartet (HTTP ${status}).` };
};

/**
 * POST `/api/material-melden` `{ token, name? }`, bei Sitzung mit
 * `Authorization: Bearer <jwt>` (der Server nimmt dann den Namen aus dem
 * Profil). Wirft nie: Netzfehler → `{ ok:false, fehler:'server', text }`.
 *
 * Der Rumpf wird erst als Text gelesen: eine HTML-Fehlerseite ließe
 * `response.json()` werfen, und der Status ginge verloren.
 */
export const melden = async (token: string, name: string, jwt?: string | null): Promise<MeldenAntwort> => {
    if (!istMaterialToken(token)) return { ok: false, fehler: 'token' };
    const n = (name ?? '').trim();
    let antwort: Response;
    try {
        antwort = await fetch('/api/material-melden', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: 'Bearer ' + jwt } : {}) },
            body: JSON.stringify(n ? { token, name: n } : { token }),
        });
    } catch (e) {
        return {
            ok: false,
            fehler: 'server',
            text: `Der Server ist nicht erreichbar (${describeError(e)}). Bitte Netz prüfen und erneut versuchen.`,
        };
    }
    const roh = await antwort.text().catch(() => '');
    let body: unknown = null;
    try {
        body = roh ? JSON.parse(roh) : null;
    } catch {
        body = null;
    }
    return meldenAntwortLesen(antwort.status, body);
};
